// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * DemoResetButton - alleen zichtbaar in DEMO_MODE
 * Wist volledig: WilsAppDB (IndexedDB), alle wilsapp_* localStorage keys,
 * alle wilsapp_* sessionStorage keys, herlaadt daarna de pagina.
 */
import { useState } from 'react'
import { Trash2 }   from 'lucide-react'
import { DEMO_MODE } from '@/config/demo'

async function fullDemoReset(): Promise<void> {
  // 1. Verwijder volledige IndexedDB
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase('WilsAppDB')
    req.onsuccess = () => resolve()
    req.onerror   = () => reject(req.error)
    req.onblocked = () => {
      // Database is geblokkeerd door een open verbinding - forceer reload
      resolve()
    }
  })

  // 2. Wis alle wilsapp_* localStorage keys
  const lsKeys = Object.keys(localStorage).filter(k => k.startsWith('wilsapp'))
  lsKeys.forEach(k => localStorage.removeItem(k))

  // 3. Wis alle wilsapp_* sessionStorage keys
  const ssKeys = Object.keys(sessionStorage).filter(k => k.startsWith('wilsapp'))
  ssKeys.forEach(k => sessionStorage.removeItem(k))

  // 4. Navigeer naar /demo/ root - altijd gevonden door server (index.html)
  //    Gebruik href i.p.v. reload() zodat deep-URL geen 404 veroorzaakt
  window.location.href = '/demo/'
}

export function DemoResetButton() {
  const [confirming, setConfirming] = useState(false)
  const [resetting,  setResetting]  = useState(false)

  if (!DEMO_MODE) return null

  async function handleConfirm() {
    setResetting(true)
    await fullDemoReset()
  }

  if (confirming) {
    return (
      <div className="flex flex-col gap-2 rounded-xl bg-status-alertBg border border-status-alertBorder px-4 py-3">
        <p className="text-xs font-semibold text-status-alertText">
          Weet u zeker dat u alle demo-data wilt wissen?
        </p>
        <p className="text-xs text-status-alertText opacity-80">
          Dit kan niet ongedaan worden gemaakt.
        </p>
        <div className="flex gap-2 mt-1">
          <button
            onClick={handleConfirm}
            disabled={resetting}
            className="flex-1 rounded-lg bg-status-alertText text-white text-xs font-semibold py-2 hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {resetting ? 'Wissen...' : 'Ja, wis alle demo-data'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={resetting}
            className="flex-1 rounded-lg border border-status-alertBorder text-status-alertText text-xs font-semibold py-2 hover:bg-status-alertBg/50 disabled:opacity-50 transition-colors"
          >
            Annuleren
          </button>
        </div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="flex items-center justify-center gap-2 w-full rounded-xl border border-status-alertBorder text-status-alertText text-xs font-semibold py-2.5 px-4 hover:bg-status-alertBg transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
      Demo resetten
    </button>
  )
}
